import React from 'react';
import moment from 'moment';
import { Modal, TitleValue, Button, Loader } from '@kaleyra/emmy';
import { useTranslation } from 'react-i18next';

import { ICredit, FilterType, ICreditDetailed, OverviewType } from '../../../../../interface';
import { AccountType, Instances, CallbackType } from '../../../../../constants';
import { REDIRECT_URL } from '../../../../../base';

import { BaseSeeMorePropsType } from '../../../templates/SeeMoreModal';
import TableFactory from '../../../templates/TableTemplate';

import Header from '../Header';

import HeaderBodyTemplate from '../../../templates/TitleSearchTable';

import Styles from './index.module.scss';
import { checkEnabledChannel, checkPrivileges, checkSubaccount, currencyFormat, numberLanguageFormat } from '../../../../../utils';
import { debounce, isEmpty } from 'lodash';
import { useSelector } from 'react-redux';
import { IStateType } from '../../../../../store/models/root.interface';

export type MapStateType = {
	data: ICredit[];
	tabularData: ICreditDetailed[];
	filters: FilterType;
	inProgress: boolean;
	smsOverviewData: OverviewType[];
};

export type DispatchPropsType = {
	onLoad: (filters: FilterType) => void;
	onSearchStrChange: (searchStr?: string) => void;
	onFilterChange: (filters: FilterType) => void;
	setFilterKey: (key: string) => void;
};

const CreditDetailed = TableFactory(Instances.creditDetailed);

const PayNowUrl = REDIRECT_URL + '/billing';

const CreditSeeMore: React.FC<
	BaseSeeMorePropsType & MapStateType & DispatchPropsType
> = ({
	tabularData,
	visible,
	filters,
	data,
	inProgress,
	onLoad,
	onFilterChange,
	onSearchStrChange,
	onClose,
	smsOverviewData,
	setFilterKey
}) => {
		const { t } = useTranslation();
		const profile = useSelector((state: IStateType) => state.profile);
		const { customer_type_id }: any = profile.fullProfile;

		React.useEffect(() => {
			onLoad(filters);
		}, []);

		const debouncedChange = React.useCallback<CallbackType>(
			debounce((value: any = "") => onSearch(value), 400),
			[]
		);

		const onSearch = (searchStr = '') => {
			onSearchStrChange(searchStr);
		};

		const getHeader = () => {
			const {
				monthly_usage: monthlyUsage = 0,
				currency_code: code = '',
				last_recharge: lastRecharge,
				spent_for_channel: sptChannels,
				balance = 0,
				next_billing: nextBilling = "0",
				amount: amount
			} = tabularData[0];

			const { account } = data[0];
			if (inProgress && isEmpty(smsOverviewData)) {
				return <Loader marginTop={70} />;
			}

			return (
				<div className={Styles.container}>
					<TitleValue
						label={account === AccountType.prepaid ? t('mainBalance') : t('creditWidgetMonthlyUsage')}
						labelPosition={'top'}
					>
						<h2>{`${code
							? account === AccountType.prepaid ? currencyFormat(Number(balance).toFixed(2), code) : currencyFormat(Number(monthlyUsage).toFixed(2), code)
							: ''
							}`}</h2>
						{!checkSubaccount() ?
							<p>
								{account === AccountType.prepaid
									? ((lastRecharge) ?
										`${t('creditWidgetLastRecharge')} ${moment.unix(Number(lastRecharge)).utc().format('DD MMM YYYY')} 
										${t('For')} ${code ? currencyFormat(amount, code) : ''}` : t('notRecharged'))
									:
									// https://kaleyra.atlassian.net/browse/HEX-7174
									// uncomment the below function incase we want to show error or 
									// warning for next billing amount and date for postpaid customers
									// (`${t('assetsWidgetNextBillingDate')} ${moment.unix(+nextBilling).format(
									// 	'DD MMM YYYY'
									// ) || ''
									// 	}`)
									<></>
								}
							</p>
							: null}
					</TitleValue>
					{checkEnabledChannel(['sms']) &&
						<TitleValue
							label={t('creditWidgetSpentForSMS')}
							labelPosition={'top'}
						>
							<h2>{`${code ? currencyFormat(Number(sptChannels?.sms).toFixed(2) || 0, code) : ''
								}`}</h2>
						</TitleValue>
					}
					{checkEnabledChannel(['voice']) &&
						<TitleValue
							label={t('creditWidgetSpentForVoice')}
							labelPosition={'top'}
						>
							<h2>{`${code
								? currencyFormat(Number(sptChannels?.voice).toFixed(2) || 0, code)
								: ''
								}`}</h2>
						</TitleValue>
					}
					{smsOverviewData[0]?.channel_status.whatsapp && checkEnabledChannel(['whatsapp']) ?
						<TitleValue
							label={t('creditWidgetSpentForWhatsapp')}
							labelPosition={'top'}
						>
							<h2>{`${code
								? currencyFormat(Number(sptChannels?.whatsapp).toFixed(2) || 0, code)
								: ''
								}`}</h2>
						</TitleValue>
						: null
					}
					{account === AccountType.prepaid && (
						<a href={PayNowUrl}>
							{checkPrivileges('billing', 'topup') &&
								<Button type="secondary">
									{data[0].account === AccountType.prepaid
										? t('creditWidgetTopupNow')
										: ''}
								</Button>}
						</a>
					)}
				</div>
			);
		};

		const getBody = () => {
			const { timerange } = filters;
			const { start_time: start, end_time: end } = timerange || {};
			let whatsappStatus = true;
			if (data && data.length) {
				whatsappStatus = data[0]?.channel_status.whatsapp;
			}

			return (
				<>
					{tabularData.length > 0 && getHeader()}
					<HeaderBodyTemplate
						title={`${t('creditStatusWidgetHeader')} - ${moment.unix(
							+start
						).format('DD MMM YYYY')} to ${moment.unix(+end).format(
							'DD MMM YYYY'
						)}`}
						onChange={debouncedChange}
					>
						<CreditDetailed
							columns={[
								{
									header: t('dateHeader'),
									accessor: 'label',
									Cell: (({ original }) => moment.unix(original.label).format('DD MMM YYYY'))
								},
								{
									header: t('channelTypevoice'),
									accessor: 'services_usage.voice',
									alignHeader: 'right',
									hide: !checkEnabledChannel(['voice']),
									Cell: ({ value }) =>
										numberLanguageFormat(value),
								},
								{
									header: t('channelTypesms'),
									accessor: 'services_usage.sms',
									alignHeader: 'right',
									Cell: ({ value }) =>
										numberLanguageFormat(value),
								},
								{
									header: t('creditLookUp'),
									accessor: 'services_usage.lookup',
									alignHeader: 'right',
									Cell: ({ value }) =>
										numberLanguageFormat(value),
								},
								{
									header: t('creditWidgetNumbers'),
									accessor: 'services_usage.numbers',
									alignHeader: 'right',
									hide: checkSubaccount(),
									Cell: ({ value }) =>
										numberLanguageFormat(value),
								},
								{
									header: t('channelTypewhatsapp'),
									accessor: 'services_usage.whatsapp',
									alignHeader: 'right',
									Cell: ({ value }) =>
										numberLanguageFormat(value),
									hide: !whatsappStatus || !checkEnabledChannel(['whatsapp']),
								},
								{
									header: t('creditWidgetTotalAmount'),
									accessor: 'services_usage.total_amount',
									alignHeader: 'right',
									Cell: ({ value }: { value: number }) => (
										<div className={Styles.lastCol}>
											{numberLanguageFormat(value)}
										</div>
									),
								},
							]}
							filters={filters}
							searchTerm={'label'}
							property={''}
						/>
					</HeaderBodyTemplate>
				</>
			);
		};

		return (
			<Modal
				visible={visible}
				title={
					<div className={Styles.titleContainer}>
						<Header
							filters={filters}
							onFilterChange={onFilterChange}
							showDownload
							account={data[0].account}
							setFilterKey={(key: string) => setFilterKey(key)}
						/>
					</div>
				}
				body={getBody()}
				onClose={onClose}
				type="fullscreen"
			/>
		);
	};

export default CreditSeeMore;
